import { z } from "zod";
/**
 * Canonical UX presentation model (CANONICAL_UX_CONTRACT_v1.0 §3).
 * Presentation adapter output only — not a second lifecycle or eligibility engine.
 */
export const CanonicalPresentationConditionSchema = z.enum([
    "READY",
    "WAITING",
    "IN_PROGRESS",
    "NEEDS_ATTENTION",
    "COMPLETE",
]);
export const CanonicalPresentationRoleSchema = z.enum(["AGENCY", "CLIENT"]);
export const CanonicalAgencyProjectTaskSchema = z.enum([
    "REVIEW_BUSINESS_INFORMATION",
    "OPEN_CLIENT_LINK",
    "REVIEW_CLARIFICATION",
    "REVIEW_READINESS",
]);
export const CanonicalPrimaryActionOpenAgencyProjectTaskSchema = z
    .object({
    intent: z.literal("OPEN_AGENCY_PROJECT_TASK"),
    task: CanonicalAgencyProjectTaskSchema,
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Route Agency to existing change-work surface for an exact request (ENR-UX-007). */
export const CanonicalPrimaryActionOpenAgencyChangeRequestWorkSchema = z
    .object({
    intent: z.literal("OPEN_AGENCY_CHANGE_REQUEST_WORK"),
    projectId: z.string().min(1),
    changeRequestId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Route Client to existing exact revised-preview review surface (ENR-UX-007). */
export const CanonicalPrimaryActionOpenClientRevisedPreviewSchema = z
    .object({
    intent: z.literal("OPEN_CLIENT_REVISED_PREVIEW"),
    previewVersionId: z.string().length(64),
    label: z.string().min(1),
})
    .strict();
/** Route Client to existing intake surface (ENR-UX-008 Slice A). */
export const CanonicalPrimaryActionOpenClientIntakeSchema = z
    .object({
    intent: z.literal("OPEN_CLIENT_INTAKE"),
    label: z.string().min(1),
})
    .strict();
/** Route Client to existing clarification surface (ENR-UX-008 Slice A). */
export const CanonicalPrimaryActionOpenClientClarificationSchema = z
    .object({
    intent: z.literal("OPEN_CLIENT_CLARIFICATION"),
    label: z.string().min(1),
})
    .strict();
/** Agency starts the authoritative build/generation pipeline (ENR-UX-009 Slice B). */
export const CanonicalPrimaryActionStartProjectBuildSchema = z
    .object({
    intent: z.literal("START_PROJECT_BUILD"),
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Agency retries a failed, stopped, or delayed build (ENR-UX-009 Slice B). */
export const CanonicalPrimaryActionRetryProjectBuildSchema = z
    .object({
    intent: z.literal("RETRY_PROJECT_BUILD"),
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Route Agency to review a quality-gate failure needing attention (ENR-UX-009 Slice B). */
export const CanonicalPrimaryActionOpenQualityGateReviewSchema = z
    .object({
    intent: z.literal("OPEN_QUALITY_GATE_REVIEW"),
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Agency opens an exact ready or superseded preview (ENR-UX-009 Slice B). */
export const CanonicalPrimaryActionOpenExactPreviewSchema = z
    .object({
    intent: z.literal("OPEN_EXACT_PREVIEW"),
    projectId: z.string().min(1),
    previewVersionId: z.string().length(64),
    label: z.string().min(1),
})
    .strict();
/** Agency shares an exact ready preview with the client (ENR-UX-009 Slice B). */
export const CanonicalPrimaryActionShareExactPreviewSchema = z
    .object({
    intent: z.literal("SHARE_EXACT_PREVIEW"),
    projectId: z.string().min(1),
    previewVersionId: z.string().length(64),
    label: z.string().min(1),
})
    .strict();
/** Agency packages the ownership handoff after client approval (ENR-UX-009 Slice C). */
export const CanonicalPrimaryActionPackageHandoffSchema = z
    .object({
    intent: z.literal("PACKAGE_HANDOFF"),
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Agency opens a blocked/failed safe-edit attempt needing attention (ENR-UX-009 Slice D). */
export const CanonicalPrimaryActionOpenSafeEditAttentionSchema = z
    .object({
    intent: z.literal("OPEN_SAFE_EDIT_ATTENTION"),
    projectId: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/** Client opens an exact ready or superseded preview (ENR-UX-009 Slice B/C). */
export const CanonicalPrimaryActionOpenClientExactPreviewSchema = z
    .object({
    intent: z.literal("OPEN_CLIENT_EXACT_PREVIEW"),
    previewVersionId: z.string().length(64),
    label: z.string().min(1),
})
    .strict();
/** Client gives exact approval consent for a preview version (ENR-UX-009 Slice C). */
export const CanonicalPrimaryActionGiveExactApprovalSchema = z
    .object({
    intent: z.literal("GIVE_EXACT_APPROVAL"),
    previewVersionId: z.string().length(64),
    label: z.string().min(1),
})
    .strict();
/** Client opens their ownership / handoff records (ENR-UX-009 Slice C). */
export const CanonicalPrimaryActionOpenOwnershipRecordsSchema = z
    .object({
    intent: z.literal("OPEN_OWNERSHIP_RECORDS"),
    label: z.string().min(1),
})
    .strict();
/** Client opens the safe-update surface to submit a new safe edit (ENR-UX-009 Slice D). */
export const CanonicalPrimaryActionOpenSafeUpdateSchema = z
    .object({
    intent: z.literal("OPEN_SAFE_UPDATE"),
    label: z.string().min(1),
})
    .strict();
export const CanonicalPrimaryActionSchema = z.discriminatedUnion("intent", [
    CanonicalPrimaryActionOpenAgencyProjectTaskSchema,
    CanonicalPrimaryActionOpenAgencyChangeRequestWorkSchema,
    CanonicalPrimaryActionOpenClientRevisedPreviewSchema,
    CanonicalPrimaryActionOpenClientIntakeSchema,
    CanonicalPrimaryActionOpenClientClarificationSchema,
    CanonicalPrimaryActionStartProjectBuildSchema,
    CanonicalPrimaryActionRetryProjectBuildSchema,
    CanonicalPrimaryActionOpenQualityGateReviewSchema,
    CanonicalPrimaryActionOpenExactPreviewSchema,
    CanonicalPrimaryActionShareExactPreviewSchema,
    CanonicalPrimaryActionPackageHandoffSchema,
    CanonicalPrimaryActionOpenSafeEditAttentionSchema,
    CanonicalPrimaryActionOpenClientExactPreviewSchema,
    CanonicalPrimaryActionGiveExactApprovalSchema,
    CanonicalPrimaryActionOpenOwnershipRecordsSchema,
    CanonicalPrimaryActionOpenSafeUpdateSchema,
]);
export const CanonicalSecondaryActionSchema = z
    .object({
    label: z.string().min(1),
    kind: z.enum(["route", "explain", "safe_alternative"]),
})
    .strict();
export const CanonicalProgressSchema = z
    .object({
    current: z.number().int().positive(),
    total: z.number().int().positive(),
    label: z.string().min(1),
})
    .strict();
export const CanonicalPresentationModelV1Schema = z
    .object({
    schemaId: z.literal("enrein.canonicalPresentation.v1"),
    role: CanonicalPresentationRoleSchema,
    journeyStage: z.string().min(1),
    condition: CanonicalPresentationConditionSchema,
    title: z.string().min(1),
    reason: z.string().min(1),
    primaryAction: CanonicalPrimaryActionSchema.nullable(),
    secondaryAction: CanonicalSecondaryActionSchema.nullable(),
    waitingOn: z.string().min(1).nullable(),
    blockers: z.array(z.string().min(1)),
    progress: CanonicalProgressSchema.nullable(),
    expectedResult: z.string().min(1).nullable(),
    recovery: z.string().min(1).nullable(),
    help: z.string().min(1).nullable(),
    relevantRecords: z.array(z.string().min(1)),
    advancedDetails: z.string().min(1).nullable(),
})
    .strict();
//# sourceMappingURL=canonical-presentation.js.map